import {DOCUMENT} from '@angular/common';
import {NavigationEnd, Router} from '@angular/router';
import {Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {GetFlexibleBenefitsResponse} from 'app/core/responses/GetFlexibleBenefitsResponse';
import {HttpErrorResponse} from '@angular/common/http';
import {GetFlexibleBenefitsRequest} from 'app/core/requests/GetFlexibleBenefitsRequest';
import {filter, takeUntil} from 'rxjs/operators';
import {AuthenticationService} from 'app/core/service';
import {BenefitsService} from 'app/core/service/benefits.service';
import {Subject} from 'rxjs';
import {GetFixedBenefitsRequest} from 'app/core/requests/GetFixedBenefitsRequest';
import {GetFixedBenefitsResponse} from 'app/core/responses/GetFixedBenefitsResponse';
import {BenefitHistoryService} from 'app/core/service/benefit-history.service';
import {GetHistoryOrdersResponse} from 'app/core/responses/GetHistoryOrdersResponse';
import {GetHistoryOrdersRequest} from 'app/core/requests/GetHistoryOrdersRequest';
import {CoreMediaService} from '@core/services/media.service';
import {MediaObserver} from '@angular/flex-layout';
import * as JsSearch from 'js-search';
import * as stemmer from 'porter-stemmer';
import {SubdomainInfo} from '../../../../core/models/subdomain-info';
import {PRICING_PLAN_LABEL, PricingPlan} from "../../../../core/helpers/constants";
import { TranslatePipe } from '@ngx-translate/core';
import { CashbackService } from 'app/core/service/cashback.service';

class DiacriticInsensitiveTokenizer extends JsSearch.SimpleTokenizer {
  tokenize(text) {
    const normalizedText = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return super.tokenize(normalizedText);
  }
}

interface SearchResult {
  id: number;
  name: string;
  subline: string;
  type: 'supplier' | 'flexible-benefit' | 'fixed-benefit' | 'deals' | 'history';
  icon: string;
  link: string;
  priority: number;
}

@Component({
  selector: 'app-navbar-search',
  templateUrl: './navbar-search.component.html',
  providers: [TranslatePipe],
  styleUrls: ['./navbar-search.component.scss'],
})
export class NavbarSearchComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  // Public
  public searchText = '';
  public apiData;
  public pages: SearchResult[] = [];
  public pageSearchLimit;
  public flexbileBenefits: any[];
  public fixedBenefits: any[];
  public allBenefits: SearchResult[] = [];
  public subdomain: string;
  public companyId: number = null;
  public search: JsSearch.Search;
  public tstemmer: any;
  public isMobile = false;
  public showMobileSearch = false;
  private routerSubscription: any;
  public indexMockupId = 0;
  public activeIndex = -1;
  public companyPlan = "FLEX";

  private typePriority = {
    'supplier': 1,
    'flexible-benefit': 2,
    'fixed-benefit': 3,
    'deals': 4
  };

  private typeIcons = {
    'supplier': 'codesandbox',
    'flexible-benefit': 'grid',
    'fixed-benefit': 'briefcase',
    'deals': 'percent'
  };

  // Decorators
  @ViewChild('openSearch') private _inputElement: ElementRef;
  @ViewChild('pageList') private _pageListElement: ElementRef;
  @ViewChild('mobileInput') private _inputMobile: ElementRef<HTMLInputElement>;


  @HostListener('keydown.escape') fn() {
    this.searchText = '';
  }
  @HostListener('document:click', ['$event']) clickout(event) {
    if (event.target.className === 'content-overlay' || event.target.closest('.nav-search') === null) {
      this.activeIndex = -1;
      this.searchText = '';
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.handleArrowUp();
    } else if (event.key === 'ArrowDown') {
      this.handleArrowDown();
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      this.navigatePage(this.pages[this.activeIndex].link);
    }
  }

  /**
   *
   * @param document
   * @param router
   * @param _searchService
   */
  constructor(
    @Inject(DOCUMENT) private document,
    private router: Router,
    private mediaObserver: MediaObserver,
    private coreMediaService: CoreMediaService,
    private translate: TranslatePipe,
    private authenticationService: AuthenticationService,
    private benefitsService: BenefitsService,
    private benefitHistoryService: BenefitHistoryService,
    private cashbackService: CashbackService,

  ) { }

  /**
   * On init
   */
  ngOnInit(): void {
    this.companyId = this.authenticationService.currentUserValue.companyId;
    this.subdomain = this.authenticationService.currentUserValue.companySubdomain;
    this.tstemmer = stemmer.stemmer;
    this.initSearchTokenization();
    if (this.authenticationService.currentUserCompanyInfoValue && this.authenticationService.currentUserCompanyInfoValue.companyHrEmail) {
      this.loadSearchItems(this.authenticationService.currentUserCompanyInfoValue);
    } else {
      this.authenticationService.currentUserCompanyInfo.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: value => this.loadSearchItems(value)
        });
    }

    this.subscribeToScreenChanges();
    if(!this.isMobile){
      this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this._inputElement.nativeElement.focus();
        });
    }
  }

  // TODO: Instead of loading all benefits here, we should implement
  //  a search API endpoint that in BE that caches and returns results based on the search query.
  //  This endpoint should be called by an authorized user as not all benefits are available across companies
  //  due to different restrictions on overrides
  //  As the cache can grow rapidly it would be wise to implement a limit of the results available and set up
  //  an eviction policy such as LRU
  loadSearchItems(companyInfo: SubdomainInfo): void {
    this.allBenefits = [];
    this.indexMockupId = 0;
    this.search = new JsSearch.Search('id');
    this.initSearchTokenization();

    this.companyPlan = PRICING_PLAN_LABEL[companyInfo.pricingPlan];
    if (companyInfo.hasFlexibleBenefits) {
      this.getFlexibleBenefits();
    }
    if (companyInfo.hasFixedBenefits) {
      this.getFixedBenefits();
    }
    if (companyInfo && companyInfo.hasCashbackBudget) {
      this.getDeals();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  subscribeToScreenChanges() {
    this.coreMediaService.onMediaUpdate.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const isSmall = this.mediaObserver.isActive('bs-lt-lg');
        this.isMobile = isSmall === true;
      });
  }


  /**
   * Search Tokenization
   */
  initSearchTokenization() {
    this.search = new JsSearch.Search('id');

    // Create a custom tokenizer chain that first normalizes text (removes diacritics)
    // and then applies stemming
    const diacriticTokenizer = new DiacriticInsensitiveTokenizer();
    this.search.tokenizer = new JsSearch.StemmingTokenizer(this.tstemmer, diacriticTokenizer);

    this.search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy();
    this.search.addIndex('name');
  }

  /**
   * Search Arrow navigation
   */

  handleArrowUp() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.setFocusOnActiveItem();
    }
  }

  handleArrowDown() {
    if (this.activeIndex < this.pages.length - 1) {
      this.activeIndex++;
      this.setFocusOnActiveItem();
    }
  }

  setFocusOnActiveItem() {
    if (!this._pageListElement || !this._pageListElement.nativeElement) {
      return;
    }

    const activeElement = this._pageListElement.nativeElement.querySelector('.auto-suggestion.active');
    if (activeElement) {
      const container = this._pageListElement.nativeElement;
      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();
      const offset = elementRect.top - containerRect.top;

      if (offset < 0 || offset + elementRect.height > containerRect.height) {
        const targetScroll = container.scrollTop + offset - containerRect.height / 3 + elementRect.height;
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        });
      }
    }
  }


  /**
   * Toggle Search
   */
  clearSearch() {
    this.activeIndex= -1;
    this.searchText = '';
    this.pages = [];
  }

  navigatePage(link) {
    this.router.navigate(['/' + this.subdomain + link]);
    this.clearSearch();
    if(this.isMobile){
      this.toggleMobileSearch(false);
    }
  }

  // GET FLEXIBLE BENEFITS
  public getFlexibleBenefits() {
    const params: GetFlexibleBenefitsRequest = {
      paginationCriteria: {
        start: 0,
        // TODO: Implement a (caching) mechanism to avoid loading all benefits at once.
        //  Other approaches:
        //    Retrieve only the necessary data for searching functionality
        //    Move the searching operation in BE (to be triggered after user stops typing)
        //  For now, we set a high limit to ensure all benefits are loaded for search functionality.
        limit: 4000,
      },
      companyId: this.companyId,
    };
    this.benefitsService
      .getUserFlexibleBenefits(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onGetFlexibleBenefitsSuccess(data);
        },
        (error) => {
          this.onFlexibleBenefitsFail(error);
        }
      );
  }

  public onGetFlexibleBenefitsSuccess(data: GetFlexibleBenefitsResponse) {
    const allFlexibleBenefits: SearchResult[] = [];
    const allProviders: SearchResult[] = [];
    const existingProviders = {};

    data.responseObject.data.forEach((b) => {
      allFlexibleBenefits.push({
        id: this.indexMockupId++,
        name: b.name,
        subline: 'HEADER.SEARCH_RESULT_FLEXIBLE_BENEFITS',
        type: 'flexible-benefit',
        icon: this.typeIcons['flexible-benefit'],
        link: `/flexible-benefits-info/${b.providerId}/${b.id}`,
        priority: this.typePriority['flexible-benefit']
      });

      if(!existingProviders[b.providerId]){
        allProviders.push({
          id: this.indexMockupId++,
          name: b.providerName,
          subline: 'HEADER.SEARCH_RESULT_SUPPLIERS',
          type: 'supplier',
          icon: this.typeIcons['supplier'],
          link: `/flexible-supplier-info/${b.providerId}`,
          priority: this.typePriority['supplier']
        });
      }
      existingProviders[b.providerId] = true;
    });

    const combinedResults = [...allProviders, ...allFlexibleBenefits];
    this.allBenefits = [...this.allBenefits, ...combinedResults];
    this.search.addDocuments(combinedResults);
  }

  public onFlexibleBenefitsFail(error: HttpErrorResponse) { }


  // GET FIXED BENEFITS
  public getFixedBenefits() {
    const params: GetFixedBenefitsRequest = {
      paginationCriteria: {
        start: 0,
        limit: 100,
      },
      companyId: this.companyId,
      orderByRank: true
    };
    this.benefitsService
      .getUserFixedBenefits(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: value => this.onGetFixedBenefitsSuccess(value),
        error: error => this.onFixedBenefitsFail(error)
      });
  }

  public onGetFixedBenefitsSuccess(data: GetFixedBenefitsResponse) {
    const fixedBenefits: SearchResult[] = data.responseObject.data.map(b => {
      const benefit = b as any;
      return {
        id: this.indexMockupId++,
        name: benefit.name,
        subline: 'HEADER.SEARCH_RESULT_CORE_BENEFITS',
        type: 'fixed-benefit',
        icon: this.typeIcons['fixed-benefit'],
        link: `/fixed-benefits-info/${benefit.companyFixedBenefitId}`,
        priority: this.typePriority['fixed-benefit']
      };
    });
    this.allBenefits = [...this.allBenefits, ...fixedBenefits];
    this.search.addDocuments(fixedBenefits);
  }

  public onFixedBenefitsFail(error: HttpErrorResponse) {
  }

  // GET DEALS
  public getDeals() {
      this.cashbackService.getSummaryForShops()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: value => this.onGetDealsSuccess(value),
          error: error => this.onDealsFail(error)
        });
  }

  public onGetDealsSuccess(data: any[]) {
    const deals: SearchResult[] = data
      .filter(shop => shop.couponCount > 0 || shop.hasCashback)
      .map(shop => ({
        id: this.indexMockupId++,
        name: shop.name,
        subline: 'HEADER.SEARCH_RESULT_DEALS',
        type: 'deals',
        icon: this.typeIcons['deals'],
        link: `/deals/${shop.id}`,
        priority: this.typePriority['deals']
      }));

    this.allBenefits = [...this.allBenefits, ...deals];
    this.search.addDocuments(deals);
  }

  public onDealsFail(error: any) {
    // Handle error if needed
  }

  /**
   * Search Update
   *
   * @param event
   */
  searchUpdate(event) {
    let val = event.target.value.toLowerCase();
    val = val.trim();
    val = val.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    this.pages = [];
    if (val.length > 1) {
      const searchResults = this.search.search(val) as SearchResult[];
      this.pages = searchResults.sort((a, b) => a.priority - b.priority);
    } else {
      this.activeIndex = -1;
    }
  }

  toggleMobileSearch(val){
    if(this.isMobile){
      this.showMobileSearch = val;
      setTimeout(() => {
        this._inputMobile.nativeElement.focus();
      }, 100);
      if(!val){
        this.clearSearch();
      }
    }
  }
}
